import { useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { passwordPattern, RouteConstants } from "../../../constants";
import authService from "../../../services/authentication.service";
import {
  Button,
  FormWarning,
  Loader,
  PasswordStrength,
} from "@shltelemedicine/shl-ui-lib";
import TextFormInput from "../../Shared/TextInput/TextFormInput";
import styles from "./ChangePasswordForm.module.css";
import PasswordRules from "../PasswordRules/PasswordRules";

enum ChangePasswordFormKeys {
  OldPassword = "oldPassword",
  NewPassword = "newPassword",
  NewPasswordConfirmation = "newPasswordConfirmation",
}

type ChangePasswordFormInputs = {
  [key in ChangePasswordFormKeys]: string;
};

const ChangePasswordForm = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [errChangingPassword, setErrChangingPassword] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors: formErrors },
  } = useForm<ChangePasswordFormInputs>();

  // Adding refs for validation
  const oldPasswordRef = useRef<string>("");
  oldPasswordRef.current = watch(ChangePasswordFormKeys.OldPassword, "");

  const newPasswordRef = useRef<string>("");
  newPasswordRef.current = watch(ChangePasswordFormKeys.NewPassword, "");

  const onSubmit = async ({
    oldPassword,
    newPassword,
    newPasswordConfirmation,
  }: ChangePasswordFormInputs) => {
    try {
      setErrChangingPassword(false);
      setLoading(true);

      const isSuccess = await authService.changePassword(
        oldPassword,
        newPassword
      );

      if (!isSuccess) {
        setErrChangingPassword(true);
        return;
      }
      navigate(RouteConstants.LOGIN_PATH);
    } finally {
      setLoading(false);
    }
  };

  const title = !errChangingPassword ? (
    <FormattedMessage
      id="CHANGE_PASSWORD.TITLE"
      defaultMessage="Your password has expired.<br></br>You must change your password before continuing"
      values={{
        br: () => <br></br>,
      }}
    />
  ) : (
    <FormattedMessage
      id="CHANGE_PASSWORD.ERROR"
      defaultMessage="Error while trying to changing password.<br></br>Please try again."
      values={{
        br: () => <br></br>,
      }}
    />
  );

  return (
    <Form className="z-index-front py-2 font-md align-self-center mb-2 d-flex flex-column">
      {!loading && <FormWarning className="mb-3" message={title} />}
      {loading && (
        <div className="p-2 mb-3">
          <Loader />
        </div>
      )}
      <div className="mb-3">
        <div className="d-flex align-items-center">
          <Form.Label className={`${styles.label} app-form-label mb-0`}>
            <span className="prim-color">*</span>
            <FormattedMessage
              id="CHANGE_PASSWORD.OLD_PASSWORD"
              defaultMessage="Old Password"
            />
            :
          </Form.Label>
          <TextFormInput
            name={ChangePasswordFormKeys.OldPassword}
            control={control}
            rules={{
              required: true,
            }}
            input={{
              className: styles.input,
              type: "password",
              isInvalid: !!formErrors?.oldPassword,
            }}
          />
        </div>
      </div>
      <div className="mb-3">
        <div className="d-flex align-items-center">
          <Form.Label className={`${styles.label} app-form-label mb-0`}>
            <span className="prim-color">*</span>
            <FormattedMessage
              id="CHANGE_PASSWORD.NEW_PASSWORD"
              defaultMessage="New Password"
            />
            :
          </Form.Label>
          <TextFormInput
            name={ChangePasswordFormKeys.NewPassword}
            control={control}
            rules={{
              required: true,
              minLength: 8,
              pattern: passwordPattern,
              validate: {
                passwordEqualOld: (value) => value !== oldPasswordRef.current,
              },
            }}
            input={{
              className: styles.input,
              type: "password",
              isInvalid: !!formErrors?.newPassword,
            }}
          />
        </div>
      </div>
      <div className="mb-4">
        <div className="d-flex align-items-center">
          <Form.Label className={`${styles.label} app-form-label mb-0`}>
            <span className="prim-color">*</span>
            <FormattedMessage
              id="CHANGE_PASSWORD.RETYPE_PASSWORD"
              defaultMessage="Confirm Password"
            />
            :
          </Form.Label>

          <TextFormInput
            name={ChangePasswordFormKeys.NewPasswordConfirmation}
            control={control}
            rules={{
              required: true,
              validate: {
                passwordMismatch: (value) => value === newPasswordRef.current,
              },
            }}
            input={{
              className: styles.input,
              type: "password",
              isInvalid: !!formErrors?.newPasswordConfirmation,
            }}
          />
        </div>
      </div>
      <div className="mb-3 d-flex align-items-center">
        <Form.Label className={`${styles.label} app-form-label mb-0`}>
          <FormattedMessage
            id="PASSWORD_STRENGTH"
            defaultMessage="Password Strength"
          />
        </Form.Label>
        <div className="mx-2"></div>
        <div style={{ flex: "1.8" }}>
          <PasswordStrength password={newPasswordRef.current} />
        </div>
      </div>
      <PasswordRules />
      <Button
        className="mt-3 px-4 prim-bg-color align-self-end app-shadow"
        disabled={loading}
        onClick={handleSubmit(onSubmit)}
      >
        <FormattedMessage
          id="CHANGE_PASSWORD.SUBMIT_TEXT"
          defaultMessage="CHANGE"
        />
      </Button>
    </Form>
  );
};

export default ChangePasswordForm;
